import {
  ArticleList,
} from "@babackapp/customer-ui/components/article";
import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
  SectionHeader,
  ShopLogo,
} from "@babackapp/customer-ui/components/header";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@babackapp/customer-ui/components/alert";
import { useState } from "react";
import { useDraftReturn } from "../hooks/use-draft-return";
import { useWidgetParams } from "../hooks/use-widget-params";
import { useCreateRetractationReturnRequest } from "../mutations/use-create-retractation-return-request";
import { usePublicOrder } from "../queries/use-public-order";
import { EnterIcon, ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { useNavigate } from "react-router-dom";
import { RetractationSummaryLineItem } from "./retractation-summary-line-item";
import { useCustomText } from "../hooks/use-custom-text";

export function RetractationSummaryPage() {
  const { orderId, shopId, shopSlug, logoUrl } = useWidgetParams();
  const navigate = useNavigate();
  const draft = useDraftReturn(orderId);
  const { data: order } = usePublicOrder(orderId);
  const createReturnRequest = useCreateRetractationReturnRequest(orderId, shopSlug);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const t = useCustomText();

  const handleConfirm = async () => {
    if (!draft) return;
    setErrorCode(null);
    setErrorMessage(null);
    try {
      const result = await createReturnRequest.mutateAsync(draft);
      navigate(`/return-widget/${shopId}/order/${orderId}/return-requests/${result?.id}`);
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setErrorCode((e as any)?.code ?? null);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setErrorMessage((e as any)?.message ?? null);
    }
  };

  return (
    <WidgetPage>
      <Header>
        <ShopLogo src={logoUrl ?? undefined} />
        <HeaderTitle>{t("retractationSummary.title")}</HeaderTitle>
        <HeaderDescription>
          {t("retractationSummary.description")}
        </HeaderDescription>
      </Header>

      {(createReturnRequest.isError || errorCode) && (
        <Alert variant="destructive">
          <ExclamationTriangleIcon />
          <AlertTitle>
            {t(
              errorCode === "RETRACTATION_INELIGIBLE_COUNTRY"
                ? "retractation.error.ineligibleCountry.title"
                : "retractationSummary.error.title",
            )}
          </AlertTitle>
          <AlertDescription>
            {errorCode === "RETRACTATION_INELIGIBLE_COUNTRY"
              ? t("retractation.error.ineligibleCountry.description")
              : errorMessage ?? t("retractationSummary.error.description")}
          </AlertDescription>
        </Alert>
      )}

      <div className="w-full mt-4 flex flex-col gap-6">
        {(draft?.lineItems ?? []).length > 0 && (
          <div className="flex flex-col gap-2">
            <SectionHeader>{t("retractationSummary.itemsTitle")}</SectionHeader>
            <ArticleList size="sm">
              {(draft?.lineItems ?? []).map((item) => (
                <RetractationSummaryLineItem
                  key={item.lineItemId}
                  item={item}
                  orderLineItems={order?.lineItems ?? []}
                />
              ))}
            </ArticleList>
          </div>
        )}
      </div>

      <AnimatedButtonRow variants={{}}>
        <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
          <Button
            size="lg"
            onClick={handleConfirm}
            disabled={!draft || createReturnRequest.isPending}
          >
            <EnterIcon />
            {createReturnRequest.isPending ? t("common.sending") : t("retractationSummary.cta")}
          </Button>
        </AnimatedButtonContainer>
      </AnimatedButtonRow>
    </WidgetPage>
  );
}
